import React, { useCallback, useEffect, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Chip, ClassNameMap } from '@mui/material';
import { useResizeDetector } from 'react-resize-detector';
import classNames from 'classnames';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import { CustomLabelChip } from './CustomLabelChip';

type CustomLabelListProps = {
  labels: CustomLabel[];
  overflowOnClick?: (e: React.MouseEvent<HTMLElement>) => void;
  classes?: ClassNameMap;
  chipClasses?: ClassNameMap;
  multiline?: boolean;
};
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: '100%'
  },
  overflow: {
    height: theme.spacing(3),
    marginLeft: theme.spacing(1)
  },
  overflowChip: {}
}));

const useChipStyles = makeStyles((theme) => ({
  root: {
    display: 'flex'
  },
  chip: {
    margin: theme.spacing(1, 0, 0, 1)
  }
}));

export const CustomLabelsList: React.FC<CustomLabelListProps> = ({
  labels,
  classes: overrideClasses,
  chipClasses: overrideChipClasses,
  overflowOnClick,
  multiline = false
}: CustomLabelListProps) => {
  const classes = useStyles({ classes: overrideClasses });
  const chipClasses = useChipStyles({ classes: overrideChipClasses });

  const { width, ref: labelRef } = useResizeDetector();
  const [widthByLabels, setWidthByLabels] = useState<Record<string, number>>(
    {}
  );
  const [visibleChipCount, setVisibleChipCount] = useState<number>(
    labels.length
  );

  const setLabelWidth = useCallback(
    (labelId: string, width = 0) => {
      setWidthByLabels((prev) => {
        return { ...prev, [labelId]: width };
      });
    },
    [setWidthByLabels]
  );

  useEffect(() => {
    if (width) {
      let index = 0;
      let computedWidth = 0;
      for (let i = 0; i < labels.length; i++) {
        computedWidth += widthByLabels[labels[i].id];
        if (computedWidth < (width ?? 0) - 50) {
          index++;
        } else {
          break;
        }
      }
      setVisibleChipCount(index);
    }
  }, [labels, width, widthByLabels]);

  // when only labels changed, no screen changed after initial render
  useEffect(() => {
    setVisibleChipCount(labels.length);
  }, [labels]);

  const filteredLabels = useMemo(
    () => (multiline ? labels : labels.slice(0, visibleChipCount)),
    [visibleChipCount, labels, multiline]
  );

  return (
    <div
      data-testid="custom-labels-list"
      className={classes.root}
      ref={labelRef}
    >
      {filteredLabels.map((label) => (
        <CustomLabelChip
          key={label.id}
          label={label}
          setWidth={setLabelWidth}
          overrideClasses={chipClasses}
          disabled
        />
      ))}
      {!multiline && visibleChipCount < labels.length && (
        <Chip
          className={classNames(classes.overflow, classes.overflowChip)}
          data-testid="custom-label-overflow-chip-count"
          label={`+${labels.length - visibleChipCount}`}
          onClick={overflowOnClick}
        />
      )}
    </div>
  );
};
